import * as React from 'react';
import { ApolloError } from 'apollo-client';
import { Query } from 'react-apollo';
import update from 'immutability-helper';

import GET_PARTICIPANT_LIST_QUERY from 'coaching/queries/GetParticipantList.gql';
import {
  GetParticipantList,
  GetParticipantList_coaching_participants_pageInfo,
  GetParticipantList_coaching_participants_participants,
} from 'coaching/queries/__generated__/GetParticipantList';
import { useRef } from 'react';

export type Props = {
  children: (props: {
    endCursor?: string | null;
    error?: ApolloError;
    hasNextPage?: boolean;
    loading: boolean;
    onNextPage: (after?: string | null) => Promise<void>;
    participants?: GetParticipantList_coaching_participants_participants[];
  }) => React.ReactNode;
  query: string;
};

const fallbackPageInfo: GetParticipantList_coaching_participants_pageInfo = {
  hasNextPage: false,
  endCursor: null,
  __typename: 'ListPageInfo',
};

export default function GetParticipantsContainer(props: Props) {
  const currentNameQuery = useRef<string>('');
  return (
    <Query<GetParticipantList>
      query={GET_PARTICIPANT_LIST_QUERY}
      variables={{ nameQuery: props.query }}>
      {({ loading, error, data, fetchMore, ...others }) => {
        currentNameQuery.current = others.variables.nameQuery;

        const handleNextPage = async (after?: string | null) => {
          await fetchMore({
            variables: {
              after,
              nameQuery: props.query,
            },
            updateQuery: (previousResult, { fetchMoreResult, variables }) => {
              if (!fetchMoreResult) {
                return previousResult;
              }
              if (currentNameQuery.current !== variables?.nameQuery) {
                return previousResult;
              }

              const existingParticipants = previousResult.coaching_participants?.participants ?? [];
              const newParticipants = fetchMoreResult.coaching_participants?.participants ?? [];

              return update(previousResult, {
                coaching_participants: {
                  pageInfo: {
                    // pageInfo is non-nullable so set a sane fallback here to prevent type issues
                    $set: fetchMoreResult.coaching_participants?.pageInfo ?? fallbackPageInfo,
                  },
                  participants: { $set: [...existingParticipants, ...newParticipants] },
                },
              });
            },
          });
        };

        return props.children({
          endCursor: data?.coaching_participants?.pageInfo?.endCursor,
          error,
          hasNextPage: data?.coaching_participants?.pageInfo?.hasNextPage,
          loading,
          onNextPage: handleNextPage,
          participants: data?.coaching_participants?.participants,
        });
      }}
    </Query>
  );
}
